import classnames from 'classnames'
import { JSX, lazy, Suspense } from 'react'

import styles from './Icon.module.scss'

export type IconNameType =
  | 'gate'
  | 'diamond'
  | 'stars'
  | 'eye'
  | 'quotes'
  | 'circle'
  | 'leftArrow'
  | 'rightArrow'
  | 'enterArrow'
  | 'honeyComb'
  | 'longLeftArrow'
  | 'longRightArrow'
  | 'bow'
  | 'gift'
  | 'spiral'
  | 'gear'

interface IconProps {
  name: IconNameType
  size?: 'S' | 'M' | 'L'
  className?: string
  onClick?: () => void
}

const iconComponents: Record<
  IconNameType,
  React.LazyExoticComponent<React.ComponentType<any>>
> = {
  gate: lazy(() =>
    import('./icons/gate').then((mod) => ({ default: mod.Gate }))
  ),
  diamond: lazy(() =>
    import('./icons/diamond').then((mod) => ({ default: mod.Diamond }))
  ),
  stars: lazy(() =>
    import('./icons/stars').then((mod) => ({ default: mod.Stars }))
  ),
  eye: lazy(() => import('./icons/eye').then((mod) => ({ default: mod.Eye }))),
  quotes: lazy(() =>
    import('./icons/quotes').then((mod) => ({ default: mod.Quotes }))
  ),
  circle: lazy(() =>
    import('./icons/circle').then((mod) => ({ default: mod.Circle }))
  ),
  leftArrow: lazy(() =>
    import('./icons/leftArrow').then((mod) => ({ default: mod.LeftArrow }))
  ),
  rightArrow: lazy(() =>
    import('./icons/rightArrow').then((mod) => ({ default: mod.RightArrow }))
  ),
  enterArrow: lazy(() =>
    import('./icons/enterArrow').then((mod) => ({ default: mod.EnterArrow }))
  ),
  honeyComb: lazy(() =>
    import('./icons/honeyComb').then((mod) => ({ default: mod.HoneyComb }))
  ),
  longLeftArrow: lazy(() =>
    import('./icons/longLeftArrow').then((mod) => ({
      default: mod.LongLeftArrow,
    }))
  ),
  longRightArrow: lazy(() =>
    import('./icons/longRightArrow').then((mod) => ({
      default: mod.LongRightArrow,
    }))
  ),
  bow: lazy(() =>
    import('./icons/bow').then((mod) => ({
      default: mod.Bow,
    }))
  ),
  gift: lazy(() =>
    import('./icons/gift').then((mod) => ({
      default: mod.Gift,
    }))
  ),
  spiral: lazy(() =>
    import('./icons/spiral').then((mod) => ({
      default: mod.Spiral,
    }))
  ),
  gear: lazy(() =>
    import('./icons/gear').then((mod) => ({
      default: mod.Gear,
    })) 
  )
}

export function Icon({
  name,
  size,
  className,
  onClick,
}: IconProps): JSX.Element | null {
  const Component = iconComponents[name]
  if (!Component) return null

  const iconClass = classnames({
    [styles.small]: size === 'S',
    [styles.medium]: size === 'M',
    [styles.large]: size === 'L',
    [styles.default]: size === undefined,
    [className as string]: className,
  })

  return (
    <Suspense fallback={<span className={styles.loader} />}>
      <div className={iconClass} onClick={onClick}>
        <Component />
      </div>
    </Suspense>
  )
}
